import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const formatCurrency = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(Number(n) || 0);
// Fecha date-only sin correrse un dia: se fuerza hora local con T00:00:00
const fmtDate = (v?: string) => {
  if (!v) return '—';
  const raw = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(raw + 'T00:00:00') : new Date(raw);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR');
};

const STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Borrador', cls: 'bg-slate-500/15 text-slate-500' },
  sent: { label: 'Pendiente', cls: 'bg-amber-500/15 text-amber-600' },
  partial: { label: 'Parcial', cls: 'bg-blue-500/15 text-blue-600' },
  received: { label: 'Recibida', cls: 'bg-emerald-500/15 text-emerald-600' },
  cancelled: { label: 'Anulada', cls: 'bg-red-500/15 text-red-600' },
};

const PurchaseDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [receiveItems, setReceiveItems] = useState<Record<string, number>>({});
  const [receiving, setReceiving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/purchases/${id}`);
      setPurchase(r.data.purchase);
      setReceiveItems({});
    } catch {
      toast.error('No se pudo cargar la orden');
      setPurchase(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const handleReceive = async () => {
    const items = Object.entries(receiveItems)
      .map(([itemId, qty]) => ({ itemId, qty: Number(qty || 0) }))
      .filter((e) => e.qty > 0);
    if (items.length === 0) { toast.error('Ingresá una cantidad para recepcionar'); return; }
    setReceiving(true);
    try {
      const r = await axios.post(`/api/purchases/${id}/receive`, { items });
      toast.success('Recepción registrada');
      setPurchase(r.data.purchase && r.data.purchase.items ? r.data.purchase : null);
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al registrar la recepción');
    } finally { setReceiving(false); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground">Cargando...</div>;
  if (!purchase) return (
    <div className="p-6 space-y-4">
      <Button variant="outline" onClick={() => navigate('/purchases')}><ArrowLeftIcon className="h-4 w-4 mr-1" /> Volver</Button>
      <p className="text-muted-foreground">Orden no encontrada.</p>
    </div>
  );

  const st = STATUS[purchase.status] || { label: purchase.status, cls: 'bg-muted text-muted-foreground' };
  const puedeRecibir = purchase.status !== 'received' && purchase.status !== 'cancelled';

  return (
    <div className="space-y-5 p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate('/purchases')} className="shrink-0"><ArrowLeftIcon className="h-4 w-4" /></Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold tracking-tight">Orden de compra {purchase.purchaseNumber}</h2>
          <p className="text-sm text-muted-foreground">{purchase.providerName}</p>
        </div>
        <Badge variant="secondary" className={`text-xs ${st.cls}`}>{st.label}</Badge>
      </div>

      <Card>
        <CardContent className="pt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <Label className="text-xs text-muted-foreground">Fecha de emisión</Label>
            <p className="font-medium">{fmtDate(purchase.issueDate)}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Forma de pago</Label>
            <p className="font-medium capitalize">{purchase.paymentMethod?.replace('_', ' ') || '—'}</p>
          </div>
          {purchase.invoiceNumber && (
            <div>
              <Label className="text-xs text-muted-foreground">N° de comprobante</Label>
              <p className="font-medium font-mono">{purchase.invoiceNumber}</p>
            </div>
          )}
          {purchase.appointmentCode && (
            <div>
              <Label className="text-xs text-muted-foreground">OT relacionada</Label>
              <p className="font-medium font-mono text-cyan-600 dark:text-cyan-400">
                {purchase.appointmentCode}{purchase.appointmentPlate ? ` · ${purchase.appointmentPlate}` : ''}
              </p>
            </div>
          )}
          {purchase.providerDocument && (
            <div>
              <Label className="text-xs text-muted-foreground">CUIT</Label>
              <p className="font-medium">{purchase.providerDocument}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="cq-table">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="px-3 text-xs">Descripción</TableHead>
                  <TableHead className="px-3 text-xs text-right">Cant.</TableHead>
                  <TableHead className="px-3 text-xs text-right cq-hide-narrow">Recibido</TableHead>
                  <TableHead className="px-3 text-xs text-right cq-hide-narrow">Pendiente</TableHead>
                  <TableHead className="px-3 text-xs text-right">Costo Unit.</TableHead>
                  <TableHead className="px-3 text-xs text-right cq-hide-narrow">IVA</TableHead>
                  <TableHead className="px-3 text-xs text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(purchase.items || []).map((item: any, i: number) => (
                  <TableRow key={i} className="border-b">
                    <TableCell className="px-3 py-2 max-w-[280px] whitespace-normal break-words">{item.description}</TableCell>
                    <TableCell className="px-3 py-2 text-right">{item.quantity}</TableCell>
                    <TableCell className="px-3 py-2 text-right cq-hide-narrow">{item.receivedQty || 0}</TableCell>
                    <TableCell className="px-3 py-2 text-right cq-hide-narrow">{Math.max(0, item.quantity - (item.receivedQty || 0))}</TableCell>
                    <TableCell className="px-3 py-2 text-right whitespace-nowrap">{formatCurrency(item.unitCost)}</TableCell>
                    <TableCell className="px-3 py-2 text-right cq-hide-narrow whitespace-nowrap">{formatCurrency(item.taxAmount || 0)}</TableCell>
                    <TableCell className="px-3 py-2 text-right font-medium whitespace-nowrap">{formatCurrency(item.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {puedeRecibir && (
        <Card>
          <CardContent className="pt-5 space-y-3">
            <div>
              <Label className="text-sm font-medium">Recepción parcial</Label>
              <p className="text-xs text-muted-foreground">Ingresá la cantidad recibida hoy por cada ítem.</p>
            </div>
            <div className="space-y-2">
              {(purchase.items || []).map((item: any) => {
                if (!item.id) return null;
                const remaining = Math.max(0, item.quantity - (item.receivedQty || 0));
                return (
                  <div key={item.id} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-6 text-sm break-words">{item.description}</div>
                    <div className="col-span-2 text-xs text-muted-foreground text-right">Pendiente: {remaining}</div>
                    <div className="col-span-4">
                      <Input type="number" min={0} max={remaining}
                        value={receiveItems[item.id] || ''}
                        onChange={(e) => setReceiveItems({ ...receiveItems, [item.id]: Number(e.target.value) })}
                        disabled={remaining === 0} className="h-9" placeholder="Cantidad" />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button onClick={handleReceive} disabled={receiving}>{receiving ? 'Registrando...' : 'Registrar recepción'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-5 space-y-2">
          <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">{formatCurrency(purchase.subtotal)}</span></div>
          <div className="flex justify-between"><span>IVA:</span><span className="font-medium">{formatCurrency(purchase.taxAmount)}</span></div>
          {Number(purchase.perceptions) > 0 && (
            <div className="flex justify-between"><span>Percepciones (IIBB y otras):</span><span className="font-medium">{formatCurrency(purchase.perceptions)}</span></div>
          )}
          <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span>{formatCurrency(purchase.total)}</span></div>
          {purchase.receivedTotal ? (
            <div className="flex justify-between text-sm text-muted-foreground"><span>Recibido:</span><span>{formatCurrency(purchase.receivedTotal)}</span></div>
          ) : null}
        </CardContent>
      </Card>

      {purchase.notes && (
        <Card>
          <CardContent className="pt-5">
            <Label className="text-xs text-muted-foreground">Notas</Label>
            <p className="mt-1">{purchase.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PurchaseDetail;
